import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { passwordStrength } from "@/utils/passwordStrength";

/**
 * `/reset-password?token=...` page.
 *
 * Flow:
 *   1. Read `token` from query string. If missing → show error + login link.
 *   2. Collect new password (and confirm) → POST /auth/reset-password.
 *   3. On success → success card with auto-redirect to /login after 2s.
 *
 * Backend invalidates ALL existing sessions on success so the user must
 * re-login with the new password from any device.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate("/login"), 2000);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset token is missing. Please request a new link.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    const s = passwordStrength(password);
    if (!s.meets) {
      toast.error(s.hint || "Password is too weak");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  const isValid = password && confirm && password === confirm && passwordStrength(password).meets;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-gray-200 shadow-lg rounded-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-4">
            <img src="/wokelo-logo.png" alt="Wokelo" className="h-8 mx-auto" />
          </div>

          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Password reset</h1>
              <p className="text-sm text-gray-500 mt-2">Redirecting you to log in…</p>
            </div>
          ) : !token ? (
            <div className="text-center py-6" data-testid="reset-no-token">
              <h1 className="text-xl font-semibold text-gray-900">Invalid reset link</h1>
              <p className="text-sm text-gray-500 mt-2">
                This reset link is missing or malformed. Please request a new one.
              </p>
              <Link to="/login" className="inline-block mt-4 text-sm text-teal-600 hover:text-teal-700 font-medium">
                Back to login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="text-xl font-semibold text-gray-900">Set a new password</h1>
                <p className="text-sm text-gray-500 mt-1">Choose a password with at least 8 characters, including a letter and a number.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-11"
                    data-testid="new-password-input"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password && (
                  <PasswordStrengthMeter value={password} testid="reset-pwd-strength" />
                )}
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                  data-testid="confirm-password-input"
                  autoComplete="new-password"
                />
                <Button
                  type="submit"
                  disabled={loading || !isValid}
                  className={`w-full h-12 rounded-lg font-medium transition-all ${
                    isValid ? "bg-gray-900 hover:bg-gray-800 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
                </Button>
              </form>

              <div className="text-center mt-6 pt-6 border-t border-gray-100">
                <Link to="/login" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                  Back to login
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
